// frontend/src/pages/AdminVerificationRequests.tsx
import React, { useEffect, useState } from 'react';
import axios from 'axios';
const LazyVerificationBadge = React.lazy(() => import('../components/VerificationBadge'));
import { API_URL } from '../config/api';

const API = API_URL;

type Req = any;

type Props = { token?: string; onNavigate?: (view: string) => void };

export default function AdminVerificationRequests({ token, onNavigate }: Props) {
  const [requests, setRequests] = useState<Req[]>([]);
  const [status, setStatus] = useState<string>('pending');
  const [category, setCategory] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [tierById, setTierById] = useState<Record<string, string>>({});

  async function load() {
    if (!token) return;
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      const params: any = {};
      if (status) params.status = status;
      if (category) params.category = category;
      const r = await axios.get(`${API}/verifications/requests`, { headers, params });
      setRequests(r.data?.requests || []);
    } catch (e: any) {
      alert(e?.response?.data?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [token]);

  function defaultTierFor(req: Req): string {
    if (req?.desiredTier) return req.desiredTier;
    return req.category === 'venue' ? 'verified_venue' : 'professional';
  }

  async function approve(req: Req) {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    const tier = tierById[req._id] || defaultTierFor(req);
    try {
      await axios.post(`${API}/verifications/requests/${req._id}/approve`, { tier }, { headers });
      await load();
    } catch (e: any) {
      alert(e?.response?.data?.message || 'Approve failed');
    }
  }

  async function reject(req: Req) {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    const notes = prompt('Rejection notes (optional):', 'Insufficient evidence');
    try {
      await axios.post(`${API}/verifications/requests/${req._id}/reject`, { decisionNotes: notes }, { headers });
      await load();
    } catch (e: any) {
      alert(e?.response?.data?.message || 'Reject failed');
    }
  }

  return (
    <div className="min-h-screen themed-page p-4 sm:p-6">
      <div className="max-w-3xl mx-auto space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-heading">Admin: Verification Requests</h2>
          {onNavigate && (
            <button className="px-3 py-2 text-xs rounded-md border" style={{ borderColor: 'var(--border)' }} onClick={() => onNavigate('dashboard')}>Back</button>
          )}
        </div>
        <div className="themed-card p-4 rounded-xl space-y-3">
          <div className="flex items-center gap-3">
            <label className="text-sm">Status</label>
            <select className="input px-3 py-2 text-sm" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
            <label className="text-sm">Category</label>
            <select className="input px-3 py-2 text-sm" value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="">All</option>
              <option value="professional">Professional</option>
              <option value="venue">Venue</option>
            </select>
            <button className="btn" onClick={load} disabled={loading}>{loading ? 'Loading…' : 'Refresh'}</button>
          </div>
        </div>
        <div className="themed-card p-4 rounded-xl">
          {requests.length === 0 ? (
            <div className="text-xs text-theme-secondary">No requests found.</div>
          ) : (
            <ul className="divide-y" style={{ borderColor: 'var(--border)' }}>
              {requests.map((r) => (
                <li key={r._id} className="py-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-semibold text-heading">{r.category} → {r.desiredTier || '—'}</div>
                      <div className="text-xs text-theme-secondary inline-flex items-center gap-2">
                        <span>Applicant: {r.applicant?.name || r.applicant?.email || r.applicant?._id}</span>
                        {r.status !== 'pending' && r.category === 'professional' && (
                          <React.Suspense fallback={null}>
                            <LazyVerificationBadge kind="user" tier={r.grantTier as any} />
                          </React.Suspense>
                        )}
                      </div>
                      {r.venue ? (
                        <div className="text-xs text-theme-secondary inline-flex items-center gap-2">
                          <span>Venue: {r.venue?.name || r.venue?._id}</span>
                          {r.status !== 'pending' && r.category === 'venue' && (
                            <React.Suspense fallback={null}>
                              <LazyVerificationBadge kind="venue" tier={r.grantTier as any} />
                            </React.Suspense>
                          )}
                        </div>
                      ) : null}
                      <div className="text-xs text-theme-secondary">Status: {r.status}</div>
                      {r.message ? <div className="text-xs mt-1">Note: {r.message}</div> : null}
                    </div>
                    <div className="flex items-center gap-2">
                      {r.status === 'pending' ? (
                        <>
                          <select
                            className="input px-3 py-2 text-xs"
                            value={tierById[r._id] ?? defaultTierFor(r)}
                            onChange={(e) => setTierById((prev) => ({ ...prev, [r._id]: e.target.value }))}
                            aria-label="Grant tier"
                          >
                            {r.category === 'professional' ? (
                              <>
                                <option value="professional">Professional</option>
                                <option value="certified_professional">Certified Professional</option>
                              </>
                            ) : (
                              <>
                                <option value="verified_venue">Verified Venue</option>
                                <option value="certified_venue">Certified Venue</option>
                              </>
                            )}
                          </select>
                          <button className="px-3 py-2 text-xs rounded-md border" style={{ borderColor: 'var(--border)' }} onClick={() => approve(r)}>Approve</button>
                          <button className="px-3 py-2 text-xs rounded-md border" style={{ borderColor: 'var(--border)' }} onClick={() => reject(r)}>Reject</button>
                        </>
                      ) : (
                        <span className="text-xs text-theme-secondary">{r.status}</span>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
